<template>
  <div :class="rootClasses" :style="[rootStyles, { position: 'relative' }]">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />

    <div
      v-if="confirmEmail && editing"
      class="email-verification__builder-info"
    >
      <TickMarkRoundedIcon />
      <div class="email-verification__builder-info__content">
        <span> Email Confirmation is On! </span>

        <span class="highlight" @click="otpView = !otpView">
          <EyeIcon color="var(--color-success-700)" />
          {{ emailVerifying ? "Preview Email Field View" : "Preview OTP View" }}
        </span>
      </div>
    </div>

    <AnalyticsWrapper>
      <Transition :name="fieldTransitionName">
        <EmailVerification
          v-if="confirmEmail && emailVerifying"
          :key="'verification'"
          class="email-verification"
          :item="item"
          :styling="styling"
          :uuid="uuid"
          :editing="editing"
          @verification-complete="handleEmailVerified"
        />

        <div v-else :key="'input'" :class="layoutClasses">
          <div
            v-if="label || description"
            class="formester-label-description-wrapper"
            :style="labelDescriptionWrapperStyles"
          >
            <label
              :class="labelClasses"
              :style="labelStyles"
              :for="item.label + item.id"
              v-html="label"
            />
            <SmartText
              :active="active"
              :editing="editing"
              :item="item"
              :style="descriptionStyles"
              :text="description"
            />
          </div>

          <div class="d-flex gap-3 position-relative">
            <input
              :id="item.label + item.id"
              ref="shortTextRef"
              v-model="itemValue"
              v-maska="inputMask"
              :data-uuid="item.id"
              :readonly="editing"
              :class="inputClasses"
              :style="[inputStyles, fieldStyles]"
              :placeholder="placeholder"
              :name="item.label"
              :required="item.required"
              :type="item.validate"
              :min="item.min"
              :max="item.max"
              :minlength="item.minimum"
              :maxlength="item.maximum"
              :step="item.stepSize || 'any'"
              :disabled="isFieldDisabled"
              @input="validate"
              @focus="handleFocus(item)"
              @blur="handleBlur(item)"
            />

            <button
              v-if="confirmEmail && !emailVerified"
              type="button"
              class="formester-input__verify__btn"
              :style="verifyButtonStyle"
              :disabled="isEmailVerifyBtnDisabled || editing"
              @click="sendCode"
            >
              <i v-if="sendingCode" class="fa-solid fa-circle-notch fa-spin" />
              {{ item.emailConfirmationData.buttonText }}
            </button>

            <img
              v-if="confirmEmail && emailVerified"
              src="@/assets/images/check-icon.png"
              class="verified__checkmark"
              aria-hidden="true"
            />
          </div>

          <LockIndicator :item="item" />
          <ErrorMessage :item="item" />
        </div>
      </Transition>
    </AnalyticsWrapper>
  </div>
</template>

<script>
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import AnalyticsWrapper from "@/components/form_fields/shared/analytics-wrapper.vue";
import EmailVerification from "@/components/form_fields/shared/email-verification.vue";
import { set as setItem } from "lodash-es";
import { isValidEmail } from "@/helpers/utils";
import { sendVerificationCode } from "@/api/form_email_verification";
import { ElNotification } from "element-plus";
import TickMarkRoundedIcon from "@/components/icons/tick-mark-rounded.vue";
import EyeIcon from "@/components/icons/eye.vue";
import { vMaska } from "maska/vue";

export default {
  name: "ShortText",
  directives: { maska: vMaska },
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
    AnalyticsWrapper,
    EmailVerification,
    TickMarkRoundedIcon,
    EyeIcon,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    uuid: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      sendingCode: false,
      confirmedEmails: [],
      otpView: false,
      mask: null,
    };
  },
  computed: {
    inputMask() {
      if (!this.isMaskEnabled) return {};

      const mask = this.normalizeMaskString(this.item.maskFormat);
      return {
        mask: () => {
          return this.item.allowMaskOverflow ? mask + " ~" : mask;
        },
        tokens: {
          w: { pattern: /[^\s]/, multiple: true },
          "~": { pattern: /./, multiple: true },
        },
      };
    },
    isMaskEnabled() {
      return this.item.useMask && this.item.maskFormat;
    },
    placeholder() {
      if (this.isMaskEnabled && this.item.allowSmartPlaceholder) {
        return this.maskaPlaceholder(this.item.maskFormat);
      } else {
        return this.item.placeholder;
      }
    },
    isEmailVerifyBtnDisabled() {
      return (
        !this.itemValue ||
        !isValidEmail(this.itemValue) ||
        this.sendingCode ||
        this.editing
      );
    },
    emailVerifying: {
      get() {
        if (this.editing) {
          return this.otpView;
        }

        return this.item.emailConfirmationData.verifying;
      },
      set(e) {
        if (this.editing) {
          this.otpView = e;
          return;
        }

        setItem(this.item, "emailConfirmationData.verifying", e);
      },
    },
    emailVerified: {
      get() {
        return this.item.emailConfirmationData.verified;
      },
      set(e) {
        setItem(this.item, "emailConfirmationData.verified", e);
      },
    },
    inputClasses() {
      return {
        "formester-input": true,
        "formester-input__shorttext": true,
      };
    },
    verifyButtonStyle() {
      return {
        "border-color": this.styling.buttonColor || "#6d5eac",
        color: this.styling.buttonColor || "white",
        "font-family": this.styling.fontFamily || "sans-serif",
      };
    },
    confirmEmail() {
      return this.item.confirmEmail && this.item.type === "email";
    },
    fieldTransitionName() {
      if (this.editing) {
        return "fade-in";
      }

      return this.confirmEmail && this.emailVerifying ? "next" : "back";
    },
  },
  mounted() {
    if (this.confirmEmail && !this.emailVerified) {
      this.$refs.shortTextRef.setCustomValidity("Please confirm your email");
    }
  },
  methods: {
    normalizeMaskString(maskString) {
      // Collapse consecutive 'w' into a single 'w'
      return maskString.replace(/(?<!!)w+/g, "w");
    },
    maskaPlaceholder(maskString) {
      const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
      const all = letters + "0123456789";
      const randomlyChoose = (source) =>
        source[Math.floor(Math.random() * source.length)];
      const randomWord = (length = 10) =>
        Array.from({ length })
          .map(() => randomlyChoose(all))
          .join("");
      return this.normalizeMaskString(maskString)
        ?.replace(/(?<!!)#/g, () => Math.floor(Math.random() * 10))
        ?.replace(/(?<!!)\@/g, () => randomlyChoose(letters))
        ?.replace(/(?<!!)\*/g, () => randomlyChoose(all))
        ?.replace(/(?<!!)w/g, () =>
          randomWord(Math.floor(5 + 5 * Math.random())),
        )
        ?.replace(/!w/g, "w")
        ?.replace(/!#/g, "#")
        ?.replace(/!\@/g, "@")
        ?.replace(/!\*/g, "*");
    },
    validate(event) {
      const el = event.srcElement;
      if (
        parseInt(el.value) < parseInt(el.min) ||
        parseInt(el.value) > parseInt(el.max)
      ) {
        setItem(
          this.item,
          "error",
          `Enter a value between ${el.min} to ${el.max}`,
        );
      } else {
        setItem(this.item, "error", "");
      }

      // Email Confirmation validation
      if (this.confirmEmail) {
        const isConfirmedEmail = this.confirmedEmails.includes(
          this.itemValue.toLowerCase(),
        );

        if (!isConfirmedEmail) {
          this.$refs.shortTextRef.setCustomValidity(
            "Please confirm your email",
          );
          this.emailVerified = false;
        } else {
          this.$refs.shortTextRef.setCustomValidity("");
          this.emailVerified = true;
        }
      }
    },
    async sendCode() {
      this.sendingCode = true;
      try {
        const mailAccountId =
          this.item.emailConfirmationData?.mailIntegrationId;
        await sendVerificationCode(this.uuid, this.itemValue, mailAccountId);
        this.sendingCode = false;
        this.emailVerifying = true;
      } catch (error) {
        this.sendingCode = false;
        const errorMessage =
          error.response?.data?.message ||
          "Unable to resend confirmation code. Please try again.";

        ElNotification({
          message: errorMessage,
          type: "error",
          position: "bottom-right",
        });
      }
    },
    handleEmailVerified() {
      this.emailVerified = true;
      this.confirmedEmails.push(this.itemValue.toLowerCase());
      this.$refs.shortTextRef.setCustomValidity("");
      this.$refs.shortTextRef.checkValidity();
    },
  },
};
</script>

<style scoped>
.formester-input__verify__btn {
  cursor: pointer;
  font-size: var(--answer-input-font-size);
  border-radius: var(--rounded);
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  background-color: transparent;
  padding: 10px var(--spacing-3);
  flex: none;
  max-width: 160px;
  box-shadow: none;
  transition: all 0.3s ease;
}

.formester-input__verify__btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.formester-input__verify__btn:focus {
  border-color: var(--btn-focus-color);
  box-shadow: 0 0 0 0.2rem var(--btn-focus-color);
  outline: none;
}

.formester-input__verify__btn:hover {
  transform: scale(1.03);
  box-shadow: 0 0 0 0.2rem var(--btn-focus-color);
}

.formester-input__verify__btn i {
  color: var(--primary-color);
}

/* Transition */
.next-enter-active {
  animation: next 0.3s ease;
}

.back-enter-active {
  animation: back 0.3s ease;
}

.fade-in-enter-active {
  animation: fade-in 0.3s ease;
}

@keyframes fade-in {
  0% {
    opacity: 0;
    scale: 0.9;
  }
  100% {
    opacity: 1;
    scale: 1;
  }
}

@keyframes next {
  0% {
    opacity: 0;
    transform: translateX(100px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes back {
  0% {
    opacity: 0;
    transform: translateX(-100px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.verified__checkmark {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.email-verification__builder-info {
  display: flex;
  align-items: center;
  background-color: #ecfdf3;
  gap: var(--spacing-2);
  padding: var(--spacing-2);
  margin-bottom: var(--spacing-2);
  border-radius: var(--rounded);
  color: var(--color-secondary-text);
}

.email-verification__builder-info__content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
}

.email-verification__builder-info__content .highlight {
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  color: var(--color-success-700);
  font-size: var(--font-size-sm);
  font-style: italic;
  font-weight: var(--font-weight-regular);
}

input:disabled {
  background-color: #7d2c2c;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
